import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PaymentInfoModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const paymentFeatures = [
    {
      icon: 'ShieldCheck',
      title: 'Secure Payment Gateway',
      description: 'All payments are processed securely with delivery confirmation before release',
      color: 'text-green-600 dark:text-green-400'
    },
    {
      icon: 'Package',
      title: 'Sugar-Only Bookings',
      description: '50% advance payment required, remaining 50% on delivery',
      color: 'text-blue-600 dark:text-blue-400'
    },
    {
      icon: 'Package2',
      title: 'SmartLoad Bundle',
      description: '100% full payment required for complete sugar + transport bundle',
      color: 'text-purple-600 dark:text-purple-400'
    },
    {
      icon: 'CheckCircle2',
      title: 'Payment Release',
      description: '80% released on POD (Proof of Delivery), 20% after quality acceptance',
      color: 'text-orange-600 dark:text-orange-400'
    },
    {
      icon: 'TrendingUp',
      title: 'Invoice Discounting',
      description: 'Available for regular buyers with good payment history',
      color: 'text-indigo-600 dark:text-indigo-400'
    },
    {
      icon: 'CreditCard',
      title: 'Multiple Payment Options',
      description: 'UPI, Net Banking, Credit/Debit Cards, and RTGS/NEFT supported',
      color: 'text-pink-600 dark:text-pink-400'
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-green-600 to-green-700 text-white p-6 rounded-t-xl">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <Icon name="ShieldCheck" size={28} />
              </div>
              <div>
                <h3 className="text-2xl font-bold">Secure Payment Information</h3>
                <p className="text-sm text-white/80 mt-1">Transparent pricing & protected transactions</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <Icon name="X" size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Trust Badge */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-950/20 dark:to-blue-950/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Icon name="Award" size={24} className="text-green-600 dark:text-green-400" />
              <div>
                <p className="text-sm font-semibold text-foreground">100% Secure Transactions</p>
                <p className="text-xs text-muted-foreground">Protected by Razorpay Payment Gateway</p>
              </div>
            </div>
          </div>

          {/* Payment Features Grid */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Icon name="Info" size={20} className="text-primary" />
              Payment Features & Terms
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {paymentFeatures?.map((feature, index) => (
                <div
                  key={index}
                  className="bg-muted/50 hover:bg-muted/70 transition-colors border border-border rounded-lg p-4"
                >
                  <div className="flex items-start gap-3">
                    <div className={`${feature?.color} mt-0.5`}>
                      <Icon name={feature?.icon} size={22} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-foreground mb-1">{feature?.title}</p>
                      <p className="text-xs text-muted-foreground leading-relaxed">{feature?.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Flow */}
          <div className="space-y-3">
            <h4 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Icon name="GitBranch" size={20} className="text-primary" />
              Payment Flow
            </h4>
            <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-primary">1</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Booking Confirmation</p>
                  <p className="text-xs text-muted-foreground">Select your booking type and proceed to payment</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-primary">2</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Payment Gateway</p>
                  <p className="text-xs text-muted-foreground">Complete payment through secure Razorpay gateway</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-primary">3</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Payment on Delivery Confirmation</p>
                  <p className="text-xs text-muted-foreground">Funds secured until delivery is completed and confirmed</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-primary">4</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Automatic Release</p>
                  <p className="text-xs text-muted-foreground">80% on POD, 20% after quality acceptance</p>
                </div>
              </div>
            </div>
          </div>

          {/* Security Notice */}
          <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Lock" size={20} className="text-blue-600 dark:text-blue-400 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-1">
                  Your Security is Our Priority
                </p>
                <p className="text-xs text-blue-800 dark:text-blue-300 leading-relaxed">
                  All transactions are encrypted with 256-bit SSL. We never store your card details. Your financial information is processed directly by Razorpay, a PCI-DSS compliant payment gateway.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-card border-t border-border p-6 rounded-b-xl">
          <Button
            variant="default"
            onClick={onClose}
            iconName="CheckCircle2"
            iconPosition="left"
            fullWidth
          >
            Got It
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentInfoModal;